import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import {
  TextRegular,
  Link,
  Card,
  UtilsBlock,
  TextField,
  ButtonsBlock,
  Button,
  Checkbox,
  TextLarge,
  SuccessInpage,
  Divider,
  Title4,
} from '@hz-design-system/web-ui';
import LoginTabs from '../../../components/LoginTabs/LoginTabs';
import LoginGoogleButton from '../../../components/LoginGoogleButton/LoginGoogleButton';
import NewPasswordFields from '../../../components/NewPasswordFields/NewPasswordFields';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import { useRecaptcha, useRecaptchaHandler } from '../../../hooks/useRecaptcha';
import { useEnterpriseRecaptcha, executeRecaptcha } from '../../../hooks/useEnterpriseRecaptcha';

import Classes from './CreateAccountComponent.module.scss';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';

import submitCreateAccount from '../repositories/submitCreateAccount';
import { trackGAEvent } from '../../../utils/gaTracking';

import useToastNotification from '../../../hooks/useToastNotification';
import type { TToastNotificationProps } from '../../../components/Toast/ToastNotification';
import { ToastContainer } from 'react-toastify';

import {
  MIN_STRING_LENGTH,
  MAX_NAME_LENGTH,
  USER_EXISTS,
  USER_BLOCKED,
  GA_EVENTS,
  RECAPTCHA_EVENTS,
} from '../../../../../common/constants';
import SocialCreateAccountComponent from './SocialCreateAccountComponent';
import useThreatMetrix from '../../../hooks/useThreatMetrix';
import useUserRiskAssessment from '../../../hooks/useUserRiskAssessment';
import { ClientError } from '@ecg-marktplaats/aurora-api-core';

const CreateAccountComponent = () => {
  const { t } = useI18nContext();
  const { toast } = useToastNotification<TToastNotificationProps>();
  const {
    xsrfToken,
    threatMetrix,
    links: { loginRedirectUrl, validatedTargetUrl },
    emailNewsletterOptin,
    recaptchaSiteKey,
    recaptchaScriptUrl,
    socialAccessToken,
    isEmbeddedInMobileApp,
    experiments: { accountDefenderEnabled, disposableDomainCheckEnabled } = {},
  } = useContext(EnvironmentContext);

  const [isLoading, setIsLoading] = useState(false);
  const [startPasswordValidation, setStartPasswordValidation] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [nameValue, setNameValue] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [isChecked, setIsChecked] = useState(emailNewsletterOptin || false);
  const [showEmailToggle, setShowEmailToggle] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const TmxNoScript = useThreatMetrix(threatMetrix);

  // Initialize user risk assessment hook
  const { submitRiskAssessment } = useUserRiskAssessment();

  // Load standard reCAPTCHA script for verification
  useRecaptcha(recaptchaScriptUrl, recaptchaSiteKey);
  // Optionally load Enterprise reCAPTCHA for tracking only
  useEnterpriseRecaptcha(accountDefenderEnabled ? recaptchaSiteKey : undefined);

  // Track with Enterprise reCAPTCHA if enabled (fire and forget, do not use token for backend)
  useEffect(() => {
    if (accountDefenderEnabled && recaptchaSiteKey) {
      executeRecaptcha(recaptchaSiteKey, RECAPTCHA_EVENTS.REGISTRATION);
    }
    // Only on mount or when recaptchaSiteKey/accountDefenderEnabled changes
  }, [accountDefenderEnabled, recaptchaSiteKey]);

  const triggerSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      trackGAEvent(GA_EVENTS.UserRegistrationBegin);

      await submitCreateAccount({
        password: passwordValue,
        xsrfToken,
        email: emailValue,
        name: nameValue,
        target: validatedTargetUrl,
        emailNewsletterOptin: isChecked,
        threatMetrix,
        recaptchaToken,
      });
      trackGAEvent(GA_EVENTS.UserRegistrationSuccess);
      setShowSuccess(true);

      if (disposableDomainCheckEnabled) {
        // Submit risk assessment after creating account
        submitRiskAssessment({
          email: emailValue,
          userName: nameValue,
          xsrfToken,
        });
      }
    } catch (err) {
      trackGAEvent(GA_EVENTS.UserRegistrationFail);
      setIsLoading(false);
      setIsPasswordValid(false);
      setIsNameValid(false);
      setIsEmailValid(false);
      const error = err as ClientError;
      const errorMessage = error.message;
      const errorCode = error.code;
      const errorDetails = error.details;
      const errorDetailsDescription = (errorDetails as { details?: { description?: string } })?.details?.description;
      let errorCheck = errorMessage;
      if (typeof errorCode === 'string') {
        errorCheck = errorCode;
      }
      switch (errorCheck) {
        case USER_EXISTS:
          toast({
            type: 'error',
            description: t('pages.create_account.validation.email_exists'),
          });
          break;
        case USER_BLOCKED:
          toast({
            type: 'error',
            description: t('pages.create_account.validation.user_blocked'),
          });
          break;
        default:
          if (errorDetailsDescription) {
            toast({
              type: 'error',
              description: t(errorDetailsDescription),
            });
          } else {
            toast({
              type: 'error',
              description: t('pages.new_password.validation.unknown_server_error'),
            });
          }
      }
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailValue,
    nameValue,
    passwordValue,
    threatMetrix,
    validatedTargetUrl,
    xsrfToken,
    toast,
    t,
    isChecked,
    submitRiskAssessment,
  ]);

  useEffect(() => {
    if (isPasswordValid && isNameValid && isEmailValid) {
      triggerSubmit();
    }
  }, [isPasswordValid, isNameValid, isEmailValid, triggerSubmit]);

  const validateName = useCallback(
    (value: string) => {
      if (!isLength(value, { min: MIN_STRING_LENGTH, max: MAX_NAME_LENGTH }) || isEmail(value)) {
        setNameError(t('pages.create_account.validation.username_invalid'));
        setIsNameValid(false);
      } else {
        setNameError('');
        setIsNameValid(true);
      }
    },
    [t],
  );

  const validateEmail = useCallback(
    (value: string) => {
      if (!isEmail(value)) {
        setEmailError(t('pages.create_account.validation.email_invalid'));
        setIsEmailValid(false);
      } else {
        setEmailError('');
        setIsEmailValid(true);
      }
    },
    [t],
  );

  const handleFinishPasswordValidation = (isValid: boolean, password: string) => {
    setIsPasswordValid(isValid);
    if (isValid) {
      setPasswordValue(password);
    }
    setStartPasswordValidation(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
    validateName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(e.target.value);
    validateEmail(e.target.value);
  };

  const handleSocialLoginClick = () => {
    setIsLoading(true);
  };

  const handleSocialError = (msg: string) => {
    setIsLoading(false);
    toast({ type: 'error', description: msg });
  };

  const onSuccess = () => {
    setIsLoading(false);
  };

  const handleSubmit = useRecaptchaHandler({
    recaptchaSiteKey: recaptchaSiteKey || '',
    onSuccess: (token: string) => {
      setStartPasswordValidation(true);
      validateName(nameValue);
      validateEmail(emailValue);
      setRecaptchaToken(token);
    },
    onError: () => {
      toast({
        type: 'error',
        description: t('pages.create_account.validation.no_captcha_been_entered'),
      });
    },
  });

  const handleCancel = () => {
    let redirectLink = loginRedirectUrl;
    if (isEmbeddedInMobileApp) {
      const deepLink = t('pages.create_account.cancel_button_deeplink');
      redirectLink = deepLink;
    }
    trackGAEvent(GA_EVENTS.UserRegistrationCancel);
    window.location.assign(redirectLink);
  };

  if (socialAccessToken) return <SocialCreateAccountComponent />;

  return (
    <div className={Classes.root}>
      {showSuccess && (
        <>
          <SuccessInpage
            icon="checkmark-circle"
            title={t('pages.create_account.success.title')}
            className={Classes.successMessage}
          />
          <Card padding="l" style={{ textAlign: 'left' }}>
            <h2>{t('pages.create_account.success.secondTitle')}</h2>
            <UtilsBlock pb="m">
              <TextLarge>{t('pages.create_account.success_message', { email: emailValue })}</TextLarge>
              <TextLarge>
                <span
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: t('pages.create_account.success.cta') }}
                />
              </TextLarge>
            </UtilsBlock>
          </Card>
        </>
      )}
      {!showSuccess && (
        <>
          <TmxNoScript />
          <Card style={{ textAlign: 'left' } as React.CSSProperties}>
            {!isEmbeddedInMobileApp && <LoginTabs isCreateAccountActive={true} />}
            <div className={Classes.header}>
              {!isEmbeddedInMobileApp && <Title4>{t('pages.create_account.page_header')}</Title4>}
              <TextRegular>
                {t('pages.create_account.existing_account_tip')}
                <Link href={loginRedirectUrl}> {t('pages.create_account.existing_account_tip_link_text')}</Link>
              </TextRegular>
            </div>
            <UtilsBlock py="s" px="s" className={Classes.googleButton}>
              <LoginGoogleButton onSuccess={onSuccess} onError={handleSocialError} onClick={handleSocialLoginClick} />
            </UtilsBlock>
            <UtilsBlock px="m">
              <Divider>{t('pages.create_account.create_account_using_email')}</Divider>
            </UtilsBlock>
            <UtilsBlock pl="l" pr="l" pt="s">
              <TextField
                type="text"
                labelText={t('pages.create_account.username_field_label')}
                isRequired={true}
                onChange={handleNameChange}
                id="userName"
                value={nameValue}
                errorText={nameError}
                maxLength={30}
                max={30}
                charsRemainingText={t('pages.create_account.lengthTip')}
                charsExceededText={t('pages.create_account.lengthExceeded')}
                ariaLive="polite"
              />
            </UtilsBlock>

            <UtilsBlock pl="l" pr="l" pb="s" pt="xxs">
              <TextField
                type="email"
                labelText={t('pages.create_account.email_field_label')}
                isRequired={true}
                onChange={handleEmailChange}
                id="email"
                value={emailValue}
                errorText={emailError}
                ariaLive="polite"
              />
            </UtilsBlock>
            <UtilsBlock pl="l" pr="l" pb="s" pt="xxs">
              <NewPasswordFields
                startValidation={startPasswordValidation}
                onValidationFinished={handleFinishPasswordValidation}
                handleSubmit={handleSubmit}
              />
            </UtilsBlock>
            <UtilsBlock pl="l" pr="l" pb="s">
              {isEmbeddedInMobileApp && (
                <div
                  className={Classes.terms}
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: t('pages.create_account.terms_and_conditions_mobile') }}
                />
              )}
              {!isEmbeddedInMobileApp && (
                <>
                  <div
                    data-testid="toggle-opt-in"
                    className={Classes.terms}
                    onClick={() => setShowEmailToggle(!showEmailToggle)}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: t('pages.create_account.gdpr_subscription_disclaimer') }}
                  />
                  {showEmailToggle && (
                    <UtilsBlock pb="s" pt="xxs">
                      <Checkbox
                        value=""
                        id="gdrp_subscription_checkbox"
                        isChecked={isChecked}
                        labelText={t('pages.create_account.gdrp_subscription_checkbox_label')}
                        onChange={() => setIsChecked(!isChecked)}
                        data-testid="email-subscription"
                      />
                    </UtilsBlock>
                  )}
                  <div
                    className={Classes.terms}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: t('pages.create_account.terms_and_conditions_no_fb') }}
                  />
                </>
              )}
            </UtilsBlock>
            <ButtonsBlock>
              <Button.Secondary onClick={handleCancel}>
                {t('pages.create_account.cancel_button_title')}
              </Button.Secondary>
              <Button.Primary disabled={isLoading} onClick={handleSubmit}>
                {t('pages.create_account.submit_button_title')}
              </Button.Primary>
            </ButtonsBlock>
          </Card>
          <ToastContainer />
        </>
      )}
    </div>
  );
};

export default CreateAccountComponent;
